/**
 * errorBufferStore — global rolling buffer of console.error / console.warn /
 * unhandledrejection / window.error captures.
 *
 * The existing `useClientErrorCapture` hook already persists window-error +
 * unhandledrejection to localStorage `pcb-client-errors`. This store layers on
 * top of that with two additions:
 *
 *   1. console.error / console.warn patches → push to buffer (preserve output).
 *   2. A custom DOM event 'pcb:react-error-boundary' → React boundaries can
 *      `dispatchEvent(new CustomEvent('pcb:react-error-boundary', { detail }))`
 *      and the panel catches it.
 *
 * Each entry is structured so the panel can render it without any further
 * parsing. Bounded to 50 entries.
 */

const MAX_ENTRIES = 50;

export type ErrorBufferKind = 'error' | 'warn' | 'unhandled-rejection' | 'window-error' | 'react-boundary';

export interface ErrorBufferEntry {
  id: number;
  kind: ErrorBufferKind;
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  source?: string;
  timestamp: string;
}

type Listener = (entries: ReadonlyArray<ErrorBufferEntry>) => void;

const buffer: ErrorBufferEntry[] = [];
const listeners = new Set<Listener>();
let nextId = 1;
let installed = false;

function notify(): void {
  const snap = buffer.slice();
  for (const l of listeners) {
    try { l(snap); } catch { /* never propagate */ }
  }
}

function push(e: Omit<ErrorBufferEntry, 'id' | 'timestamp'>): void {
  buffer.push({
    ...e,
    id: nextId++,
    timestamp: new Date().toISOString(),
  });
  while (buffer.length > MAX_ENTRIES) buffer.shift();
  notify();
}

function stringifyArg(arg: unknown): string {
  if (arg == null) return String(arg);
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try { return JSON.stringify(arg); } catch { return String(arg); }
}

function joinArgs(args: unknown[]): string {
  return args.map(stringifyArg).join(' ');
}

function pickStack(args: unknown[]): string | undefined {
  for (const a of args) {
    if (a instanceof Error && a.stack) return a.stack;
  }
  return undefined;
}

export function installErrorBuffer(): void {
  if (installed) return;
  if (typeof globalThis === 'undefined') return;
  installed = true;

  // ─── console.error / console.warn ───────────────────────────────────────
  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    try {
      const stack = pickStack(args);
      const entry: Omit<ErrorBufferEntry, 'id' | 'timestamp'> = {
        kind: 'error',
        level: 'error',
        message: joinArgs(args),
      };
      if (stack !== undefined) entry.stack = stack;
      push(entry);
    } catch { /* never block console */ }
    originalError(...args);
  };

  console.warn = (...args: unknown[]) => {
    try {
      const stack = pickStack(args);
      const entry: Omit<ErrorBufferEntry, 'id' | 'timestamp'> = {
        kind: 'warn',
        level: 'warn',
        message: joinArgs(args),
      };
      if (stack !== undefined) entry.stack = stack;
      push(entry);
    } catch { /* never block console */ }
    originalWarn(...args);
  };

  // ─── Window-level handlers ──────────────────────────────────────────────
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (ev: ErrorEvent) => {
      const entry: Omit<ErrorBufferEntry, 'id' | 'timestamp'> = {
        kind: 'window-error',
        level: 'error',
        message: ev.message ?? 'window.error',
        url: window.location.href,
      };
      if (ev.error?.stack) entry.stack = String(ev.error.stack);
      else if (ev.filename) entry.source = `${ev.filename}:${ev.lineno}:${ev.colno}`;
      push(entry);
    });

    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      const reason: unknown = ev.reason;
      let message = 'unhandled rejection';
      let stack: string | undefined;
      if (reason instanceof Error) {
        message = reason.message;
        stack = reason.stack;
      } else if (typeof reason === 'string') {
        message = reason;
      } else {
        try { message = JSON.stringify(reason); } catch { /* ignore */ }
      }
      const entry: Omit<ErrorBufferEntry, 'id' | 'timestamp'> = {
        kind: 'unhandled-rejection',
        level: 'error',
        message,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      };
      if (stack !== undefined) entry.stack = stack;
      push(entry);
    });

    // React error-boundary bridge — boundaries dispatch this on catch.
    window.addEventListener('pcb:react-error-boundary', ((ev: Event) => {
      const detail = (ev as CustomEvent<{
        message?: string;
        stack?: string;
        componentStack?: string;
      }>).detail;
      const entry: Omit<ErrorBufferEntry, 'id' | 'timestamp'> = {
        kind: 'react-boundary',
        level: 'error',
        message: detail?.message ?? 'React error boundary',
      };
      if (detail?.stack) entry.stack = detail.stack;
      if (detail?.componentStack) entry.componentStack = detail.componentStack;
      push(entry);
    }) as EventListener);
  }
}

export function getErrorBufferSnapshot(): ReadonlyArray<ErrorBufferEntry> {
  return buffer.slice();
}

export function subscribeErrorBuffer(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function clearErrorBuffer(): void {
  buffer.length = 0;
  notify();
}
